import React, { useState, useEffect, useCallback, useRef } from 'react';
import { BrowserRouter, Route, Navigate, useParams, useNavigate } from 'react-router-dom';
import { FaroRoutes } from '@grafana/faro-react';
import { withLDProvider, useFlags, useLDClient } from 'launchdarkly-react-client-sdk';
import './App.css';
import SearchBar from './components/SearchBar';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import SubscribePage from './pages/SubscribePage';
import AuthCallbackPage from './pages/AuthCallbackPage';
import WatchlistPage from './pages/WatchlistPage';
import AccountPage from './pages/AccountPage';
import AdminPage from './pages/AdminPage';
import PageViewTracker from './components/PageViewTracker';
import { AuthProvider } from './context/AuthContext';
import StockOverview from './components/StockOverview';
import FinancialData from './components/FinancialData';
import DividendInfo from './components/DividendInfo';
import ScorecardInfo from './components/ScorecardInfo';
import StockComparison from './components/StockComparison';
import ScreenersPage from './components/ScreenersPage';
import LoadingSpinner from './components/LoadingSpinner';
import ErrorMessage from './components/ErrorMessage';
import FetchingMessage from './components/FetchingMessage';
import ProfileInfo from './components/ProfileInfo';
import MarketMovers from './components/MarketMovers';
import IndexCharts from './components/IndexCharts';
import config from './config';
import { faro } from './faro';
import AIAnalysisTab from './components/AIAnalysisTab';
import OptionsAnalysis from './components/OptionsAnalysis';
import PremiumPrompt from './components/PremiumPrompt';
import InstallPWAPrompt from './components/InstallPWAPrompt';
import { useAuth, getToken } from './context/AuthContext';

const { apiBaseURL } = config;

// Helper function to construct API URLs
export const getApiUrl = (endpoint) => {
  return `${apiBaseURL}${endpoint}`;
};


// Main App component that wraps everything with BrowserRouter
function App() {
  // Logo URL for ValueSieve
  const logoUrl = `/logo.png`;

  return (
    <BrowserRouter>
      <AuthProvider>
        <LDIdentifier />
        <PageViewTracker />
        <div className="App">
          <header className="App-header">
            <div className="header-content">
              <div className="logo-container" onClick={() => window.location.href = '/'} style={{ cursor: 'pointer' }}>
                <img src={logoUrl} alt="ValueSieve Logo" className="logo-image" width="40" height="40" />
                <h1>ValueSieve</h1>
              </div>
              <nav className="main-nav">
                <NavBar />
              </nav>
            </div>
          </header>

          <main className="App-content">
            <FaroRoutes>
              <Route path="/" element={<HomePage />} />
              <Route path="/stock-analyzer" element={<Navigate to="/" />} />
              <Route path="/screeners" element={<ScreenersRoute />} />
              <Route path="/screeners/:screenerId" element={<ScreenersRoute />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/signup" element={<SignupPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/subscribe" element={<SubscribePage />} />
              <Route path="/auth/callback" element={<AuthCallbackPage />} />
              <Route path="/watchlists" element={<WatchlistPage />} />
              <Route path="/account" element={<AccountPage />} />
              <Route path="/admin" element={<AdminPage />} />
              <Route path="/:symbol" element={<StockRoute />} />
            </FaroRoutes>
          </main>

          <InstallPWAPrompt />
        </div>
      </AuthProvider>
    </BrowserRouter>
  );
}

// Keeps LaunchDarkly context in sync with the logged-in user so that
// email-targeted flag rules evaluate correctly.
function LDIdentifier() {
  const { user } = useAuth();
  const ldClient = useLDClient();
  const hookRegistered = useRef(false);

  useEffect(() => {
    if (!ldClient || hookRegistered.current) return;
    hookRegistered.current = true;
    ldClient.addHook({
      getMetadata: () => ({ name: 'FaroFlagTracker' }),
      afterEvaluation(hookContext, _data, detail) {
        faro.api.pushEvent('feature_flag_evaluated', {
          'feature_flag.key': hookContext.flagKey,
          'feature_flag.variant': String(detail.value),
          'feature_flag.provider_name': 'LaunchDarkly',
        });
        return detail;
      },
    });
  }, [ldClient]);

  useEffect(() => {
    if (!ldClient) return;
    if (user?.email) {
      ldClient.identify({ kind: 'user', key: user.email, email: user.email });
    } else {
      ldClient.identify({ kind: 'user', key: 'anonymous' });
    }
  }, [user, ldClient]);

  return null;
}

function NavBar() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { pathname } = window.location;
  const [menuOpen, setMenuOpen] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  // UI hint only; the backend enforces the "admin" flag on /api/admin/stats.
  useEffect(() => {
    if (!user) { setIsAdmin(false); return; }
    fetch(`${config.apiBaseURL}/api/admin/me`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    })
      .then(r => (r.ok ? r.json() : { admin: false }))
      .then(d => setIsAdmin(d.admin === true))
      .catch(() => setIsAdmin(false));
  }, [user]);

  const go = (path) => { navigate(path); setMenuOpen(false); };

  return (
    <>
      <button className="hamburger-btn" onClick={() => setMenuOpen(o => !o)} aria-label="Menu">
        {menuOpen ? '✕' : '☰'}
      </button>
      <div className={`nav-links${menuOpen ? ' nav-links--open' : ''}`}>
        <button
          className={pathname === '/' || pathname.match(/^\/[A-Z]+$/) ? 'active' : ''}
          onClick={() => go('/')}
        >
          Stock Analyzer
        </button>
        <button
          className={pathname.includes('/screeners') ? 'active' : ''}
          onClick={() => go('/screeners')}
        >
          Screeners
        </button>
        {user ? (
          <>
            <button
              className={pathname === '/watchlists' ? 'active' : ''}
              onClick={() => go('/watchlists')}
            >
              Watchlist
            </button>
            <button
              className={pathname === '/account' ? 'active' : ''}
              onClick={() => go('/account')}
            >
              Account
            </button>
            {isAdmin && (
              <button
                className={pathname === '/admin' ? 'active' : ''}
                onClick={() => go('/admin')}
              >
                Admin
              </button>
            )}
            <button onClick={() => { logout(); go('/'); }}>
              Log Out
            </button>
          </>
        ) : (
          <button
            className={pathname === '/login' ? 'active' : ''}
            onClick={() => go('/login')}
          >
            Log In
          </button>
        )}
      </div>
    </>
  );
}

// HomePage component - displays market movers and search
function HomePage() {
  const navigate = useNavigate();
  
  const handleSearch = (symbol) => {
    navigate(`/${symbol}`);
  };
  
  const handleStockSelect = (symbol) => {
    navigate(`/${symbol}`);
  };
  
  console.log("Rendering HomePage component"); // Debug log
  
  return (
    <div className="home-page">
      <div className="home-search-container">
        <h2 className="home-title">Search for a stock</h2>
        <SearchBar onSearch={handleSearch} />
      </div>

      <IndexCharts />

      <div className="market-movers-wrapper" style={{ width: '100%', display: 'block' }}>
        <MarketMovers onStockSelect={handleStockSelect} />
      </div>
    </div>
  );
}

// ScreenersRoute component - handles screener pages
function ScreenersRoute() {
  const { screenerId } = useParams();
  const navigate = useNavigate();
  
  const handleStockSelect = (symbol) => {
    navigate(`/${symbol}`);
  };
  
  return (
    <div className="screeners-container">
      <div className="search-container">
        <SearchBar onSearch={(symbol) => navigate(`/${symbol}`)} />
      </div>
      <ScreenersPage 
        initialScreener={screenerId} 
        onStockSelect={handleStockSelect} 
      />
    </div>
  );
}

// StockRoute component - handles displaying stock data based on URL
function StockRoute() {
  const { symbol } = useParams();
  const navigate = useNavigate();
  const { offerAiAnalysis, offerOptionsTab, premiumFeaturesEnabled } = useFlags();
  const { isSubscribed } = useAuth();
  const [stockData, setStockData] = useState(null);
  const [livePrice, setLivePrice] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [fetchingSymbol, setFetchingSymbol] = useState(null);
  
  // Comparison state
  const [comparisonMode, setComparisonMode] = useState(false);
  const [comparisonStocks, setComparisonStocks] = useState([]);

  // Memoize handleStockNotFound
  const handleStockNotFound = useCallback((symbol) => {
    setError(`Stock ${symbol} not found`);
    setLoading(false);
  }, []); // No dependencies needed as it only uses setError and setLoading which are stable

  // Update fetchStockData to include handleStockNotFound in dependencies
  const fetchStockData = useCallback(async (symbol, isRetry = false) => {
    if (!symbol) return;
    
    setLoading(true);
    setError(null);
    
    if (!isRetry) {
      setStockData(null);
    }
    
    console.log(`DEBUG: Starting fetchStockData for ${symbol}, isRetry=${isRetry}`);
    
    try {
      // Fetch basic stock data
      const stocksUrl = `${apiBaseURL}/api/stock/${symbol}`;
      console.log(`DEBUG: Fetching from ${stocksUrl}`);
      
      const stockResponse = await fetch(stocksUrl);
      console.log(`DEBUG: Stock API response status: ${stockResponse.status}`);
      
      if (!stockResponse.ok) {
        // API returned an error status, handle as stock not found
        handleStockNotFound(symbol);
        return;
      }
      
      // Parse the response even if it's ok
      const stockResult = await stockResponse.json();
      console.log(`DEBUG: Received response for ${symbol}`, stockResult);
      
      if (!stockResult.stock) {
        // API returned success but with empty data, handle as stock not found
        console.log(`DEBUG: Stock result is empty`);
        handleStockNotFound(symbol);
        return;
      }
      
      // Stock was found, proceed with fetching additional data
      console.log(`DEBUG: Found stock data for ${symbol}`);
      
      // Fetch all related data in parallel
      const historicalUrl = `${apiBaseURL}/api/historical-prices/${symbol}`;
      const scorecardUrl = `${apiBaseURL}/api/scorecard/${symbol}`;
      const peersUrl = `${apiBaseURL}/api/peers/${symbol}`;
      
      const [
        incomeResponse, 
        balanceResponse, 
        cashflowResponse, 
        ratiosResponse,
        historicalResponse,
        dividendResponse,
        scorecardResponse,
        peersResponse
      ] = await Promise.all([
        fetch(`${apiBaseURL}/api/financials/income/${symbol}`),
        fetch(`${apiBaseURL}/api/financials/balance/${symbol}`),
        fetch(`${apiBaseURL}/api/financials/cashflow/${symbol}`),
        fetch(`${apiBaseURL}/api/financials/ratios/${symbol}`),
        fetch(historicalUrl),
        fetch(`${apiBaseURL}/api/dividends/${symbol}`),
        fetch(scorecardUrl),
        fetch(peersUrl)
      ]);
      
      // Process responses
      let historical = null;
      if (historicalResponse.ok) {
        try {
          historical = await historicalResponse.json();
        } catch (e) {
          console.error("DEBUG: Error parsing historical data:", e);
        }
      }
      
      let scorecard = null;
      if (scorecardResponse.ok) {
        try {
          scorecard = await scorecardResponse.json();
        } catch (e) {
          console.error("DEBUG: Error parsing scorecard data:", e);
        }
      }

      let peers = null;
      if (peersResponse.ok) {
        try {
          peers = await peersResponse.json();
        } catch (e) {
          console.error("DEBUG: Error parsing peers data:", e);
        }
      }

      // Handle the other responses
      const income = incomeResponse.ok ? await incomeResponse.json() : null;
      const balance = balanceResponse.ok ? await balanceResponse.json() : null;
      const cashflow = cashflowResponse.ok ? await cashflowResponse.json() : null;
      const ratios = ratiosResponse.ok ? await ratiosResponse.json() : null;
      const dividend = dividendResponse.ok ? await dividendResponse.json() : null;

      // If the backend is still fetching financial data or peers, poll until ready
      const isFetching = income?.message?.includes('being fetched') ||
                         balance?.message?.includes('being fetched') ||
                         cashflow?.message?.includes('being fetched') ||
                         peers?.message?.includes('being fetched');

      if (isFetching) {
        const MAX_POLLS = 12;
        const POLL_INTERVAL = 5000;
        let latestPeers = peers;

        for (let attempt = 1; attempt <= MAX_POLLS; attempt++) {
          await new Promise(resolve => setTimeout(resolve, POLL_INTERVAL));

          const [incR, balR, cfR, ratR, peersR] = await Promise.all([
            fetch(`${apiBaseURL}/api/financials/income/${symbol}`),
            fetch(`${apiBaseURL}/api/financials/balance/${symbol}`),
            fetch(`${apiBaseURL}/api/financials/cashflow/${symbol}`),
            fetch(`${apiBaseURL}/api/financials/ratios/${symbol}`),
            fetch(peersUrl),
          ]);

          const incPoll   = incR.ok   ? await incR.json()   : null;
          const balPoll   = balR.ok   ? await balR.json()   : null;
          const cfPoll    = cfR.ok    ? await cfR.json()    : null;
          const ratPoll   = ratR.ok   ? await ratR.json()   : null;
          const peersPoll = peersR.ok ? await peersR.json() : latestPeers;
          latestPeers = peersPoll;

          const stillFetching = incPoll?.message?.includes('being fetched') ||
                                balPoll?.message?.includes('being fetched') ||
                                cfPoll?.message?.includes('being fetched') ||
                                peersPoll?.message?.includes('being fetched');

          if (!stillFetching) {
            setStockData({
              stock: stockResult.stock,
              screeners: stockResult.screeners,
              inScreeners: stockResult.inScreeners,
              income: incPoll,
              balance: balPoll,
              cashflow: cfPoll,
              ratios: ratPoll,
              historical,
              dividend,
              scorecard,
              peers: peersPoll
            });
            setError(null);
            setComparisonMode(false);
            setComparisonStocks([]);
            return;
          }
        }

        // Polling timed out — use whatever peers data we have
        peers = latestPeers;
      }

      // Combine all data
      setStockData({
        stock: stockResult.stock,
        screeners: stockResult.screeners,
        inScreeners: stockResult.inScreeners,
        income,
        balance,
        cashflow,
        ratios,
        historical,
        dividend,
        scorecard,
        peers
      });
      
      // Clear any fetch-in-progress error
      setError(null);
      
      // Exit comparison mode when loading a new stock
      setComparisonMode(false);
      setComparisonStocks([]);
      
    } catch (err) {
      console.error("DEBUG: Error in fetchStockData:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [handleStockNotFound]); // Add handleStockNotFound to dependencies
  
  // Update useEffect to include fetchStockData in dependencies
  useEffect(() => {
    if (symbol) {
      setFetchingSymbol(symbol);
      fetchStockData(symbol);
    }
  }, [symbol, fetchStockData]); // Add fetchStockData to dependencies

  // Poll live price every 30s during market hours
  useEffect(() => {
    setLivePrice(null);
    if (!symbol) return;

    const isMarketOpen = () => {
      const et = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/New_York' }));
      const day = et.getDay();
      if (day === 0 || day === 6) return false;
      const mins = et.getHours() * 60 + et.getMinutes();
      return mins >= 570 && mins < 960;
    };

    if (!isMarketOpen()) return;

    const poll = async () => {
      if (document.hidden) return;
      try {
        const res = await fetch(`${apiBaseURL}/api/price/${symbol}`);
        if (!res.ok) return;
        const { price, change_percent, day_open } = await res.json();
        setLivePrice({ price, change_percent, day_open });
      } catch (_) {}
    };

    poll();
    const id = setInterval(poll, 30000);
    return () => clearInterval(id);
  }, [symbol]);

  // Handle search from the stock page - navigate to the new symbol
  const handleSearch = (newSymbol) => {
    navigate(`/${newSymbol}`);
  };

  // Function to handle peer click and fetch comparison data
  const buildComparisonStock = (stockBase, ratiosData, incomeData, balanceData, dividendData) => {
    const flat = { ...stockBase };

    if (ratiosData?.financialRatios?.length > 0) {
      Object.assign(flat, ratiosData.financialRatios[0]);
    }

    const statements = incomeData?.incomeStatements || [];
    const annual = statements.find(s => s.period === 'FY');
    const quarterly = statements.find(s => s.period?.startsWith('Q'));
    if (annual) {
      flat.eps = annual.eps;
      flat.annual_revenue = annual.revenue;
      flat.annual_earnings = annual.net_income;
    }
    if (quarterly) {
      flat.quarterly_revenue = quarterly.revenue;
      flat.quarterly_earnings = quarterly.net_income;
    }

    const balance = balanceData?.balanceSheets?.[0];
    if (balance) {
      flat.cash = balance.cash_and_equivalents;
      if (balance.total_assets && balance.total_liabilities) {
        flat.liabilities_to_assets = balance.total_liabilities / balance.total_assets;
      }
    }

    if (dividendData) {
      flat.dividend_yield = dividendData.dividendYield;
      if (dividendData.dividends?.length > 0) {
        flat.lastDividend = {
          amount: dividendData.dividends[0].amount,
          date: dividendData.dividends[0].date
        };
      }
    }

    return flat;
  };

  const handlePeerClick = async (peerSymbol) => {
    if (comparisonMode && comparisonStocks.some(stock => stock.symbol === peerSymbol)) {
      return;
    }

    setLoading(true);
    try {
      const peerResponse = await fetch(`${apiBaseURL}/api/stock/${peerSymbol}`);
      if (!peerResponse.ok) throw new Error(`Failed to fetch peer stock data for ${peerSymbol}`);

      const peerResult = await peerResponse.json();
      if (!peerResult.stock) throw new Error(`No data found for peer stock ${peerSymbol}`);

      const [incomeRes, balanceRes, ratiosRes, dividendRes] = await Promise.all([
        fetch(`${apiBaseURL}/api/financials/income/${peerSymbol}`),
        fetch(`${apiBaseURL}/api/financials/balance/${peerSymbol}`),
        fetch(`${apiBaseURL}/api/financials/ratios/${peerSymbol}`),
        fetch(`${apiBaseURL}/api/dividends/${peerSymbol}`)
      ]);

      const peerRatios   = ratiosRes.ok   ? await ratiosRes.json()   : null;
      const peerIncome   = incomeRes.ok   ? await incomeRes.json()   : null;
      const peerBalance  = balanceRes.ok  ? await balanceRes.json()  : null;
      const peerDividend = dividendRes.ok ? await dividendRes.json() : null;

      const peerStock    = buildComparisonStock(peerResult.stock, peerRatios, peerIncome, peerBalance, peerDividend);
      const currentStock = buildComparisonStock(stockData.stock, stockData.ratios, stockData.income, stockData.balance, stockData.dividend);

      setComparisonStocks([currentStock, peerStock]);
      setComparisonMode(true);
    } catch (err) {
      console.error("Error fetching peer data for comparison:", err);
      setError(`Failed to load comparison data: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };
  
  // Exit comparison mode
  const exitComparisonMode = () => {
    setComparisonMode(false);
    setComparisonStocks([]);
  };

  // Handle screener tag click
  const handleScreenerClick = (screenerSlug) => {
    // Navigate to the screeners page with the specific screener
    navigate(`/screeners/${screenerSlug}`);
  };

  return (
    <div>
      <div className="search-container">
        <SearchBar onSearch={handleSearch} initialSymbol={symbol} />
      </div>
      
      {/* Only show loading spinner if we're loading and there's no error */}
      {loading && !error && <LoadingSpinner />}
      
      {/* Different error handling based on message */}
      {error && (
        error.includes("Fetching data for") 
          ? <FetchingMessage symbol={fetchingSymbol} /> 
          : <ErrorMessage message={error} />
      )}
      
      {stockData && !comparisonMode && (
        <>
          <div className="tabs">
            <button 
              className={activeTab === 'overview' ? 'active' : ''} 
              onClick={() => setActiveTab('overview')}
            >
              Overview
            </button>
            <button 
              className={activeTab === 'profile' ? 'active' : ''} 
              onClick={() => setActiveTab('profile')}
            >
              Profile
            </button>
            <button 
              className={activeTab === 'scorecard' ? 'active' : ''} 
              onClick={() => setActiveTab('scorecard')}
            >
              Scorecard
            </button>
            <button 
              className={activeTab === 'financials' ? 'active' : ''} 
              onClick={() => setActiveTab('financials')}
            >
              Financials
            </button>
            <button 
              className={activeTab === 'dividends' ? 'active' : ''} 
              onClick={() => setActiveTab('dividends')}
            >
              Dividends
            </button>
            {offerOptionsTab && (
              <button
                className={activeTab === 'options' ? 'active' : ''}
                onClick={() => setActiveTab('options')}
              >
                Options
              </button>
            )}
            {offerAiAnalysis && (
              <button
                className={activeTab === 'ai-analysis' ? 'active' : ''}
                onClick={() => setActiveTab('ai-analysis')}
              >
                AI Analysis
              </button>
            )}
          </div>
          
          <div className="tab-content">
            {activeTab === 'overview' &&
              <StockOverview
                data={livePrice ? { ...stockData, stock: { ...stockData.stock, ...livePrice } } : stockData}
                onPeerClick={handlePeerClick}
                onScreenerClick={handleScreenerClick}
              />
            }
            {activeTab === 'profile' && <ProfileInfo data={stockData} />}
            {activeTab === 'scorecard' && <ScorecardInfo data={stockData.scorecard} />}
            {activeTab === 'financials' && <FinancialData data={stockData} />}
            {activeTab === 'dividends' && <DividendInfo data={stockData.dividend} />}
            {offerOptionsTab && activeTab === 'options' && (
              !premiumFeaturesEnabled && !isSubscribed
                ? <PremiumPrompt feature="the Options tab" inline />
                : <OptionsAnalysis symbol={symbol} />
            )}
            {offerAiAnalysis && activeTab === 'ai-analysis' && (
              !premiumFeaturesEnabled && !isSubscribed
                ? <PremiumPrompt feature="AI Analysis" inline />
                : (
                  <React.Suspense fallback={<LoadingSpinner />}>
                    <AIAnalysisTab symbol={symbol} />
                  </React.Suspense>
                )
            )}
          </div>
        </>
      )}
      
      {stockData && comparisonMode && (
        <>
          <div className="comparison-close">
            <button onClick={exitComparisonMode}>
              ← Back to {stockData.stock.symbol}
            </button>
          </div>
          <StockComparison stocks={comparisonStocks} />
        </>
      )}
    </div>
  );
}

export default withLDProvider({
  clientSideID: config.launchDarklyClientId,
  context: { kind: 'user', key: 'anonymous' },
  options: { bootstrap: 'localStorage' },
})(App);